import type {
  Client,
  QuoteRecord,
  EmailLogEntry,
  CmsDocument,
  Flight,
  ClientActivity,
  StoredNotification,
} from '../types';

const CLIENTS_KEY = 'skylux_clients_v1';
const QUOTES_KEY = 'skylux_quotes_v1';
const EMAIL_LOG_KEY = 'skylux_email_log_v1';
const CMS_KEY = 'skylux_cms_v1';
const FLIGHTS_KEY = 'skylux_flights_v1';
const NOTIFICATIONS_KEY = 'skylux_notifications_v1';
const ACTIVITIES_KEY = 'skylux_activities_v1';
const AGENT_EMAIL_BACKUP_KEY = 'skylux_agent_email_backup_v1';

function emitStorage() {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('skylux-storage'));
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown) {
  localStorage.setItem(key, JSON.stringify(value));
  emitStorage();
}

export function loadClients(seed: Client[]): Client[] {
  const stored = readJson<Client[] | null>(CLIENTS_KEY, null);
  if (stored && Array.isArray(stored)) return stored;
  writeJson(CLIENTS_KEY, seed);
  return seed;
}

export function saveClients(clients: Client[]) {
  writeJson(CLIENTS_KEY, clients);
}

export function getClientById(id: string, seed: Client[]): Client | undefined {
  return loadClients(seed).find((c) => c.id === id);
}

export function loadQuotes(): QuoteRecord[] {
  return readJson<QuoteRecord[]>(QUOTES_KEY, []).map((q) => ({
    ...q,
    paymentStatus: q.paymentStatus ?? 'unpaid',
  }));
}

export function saveQuote(record: QuoteRecord) {
  const all = loadQuotes().filter((q) => q.id !== record.id);
  all.unshift(record);
  writeJson(QUOTES_KEY, all);
}

export function getQuoteById(id: string): QuoteRecord | undefined {
  return loadQuotes().find((q) => q.id === id);
}

export function getQuotesForClient(clientId: string): QuoteRecord[] {
  return loadQuotes()
    .filter((q) => q.clientId === clientId)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

export function markQuotePaid(quoteId: string, seedClients: Client[]) {
  const q = getQuoteById(quoteId);
  if (!q || q.paymentStatus === 'paid') return;
  saveQuote({ ...q, paymentStatus: 'paid' });
  const clients = loadClients(seedClients);
  const next = clients.map((c) => (c.id === q.clientId ? { ...c, status: 'paid' as const } : c));
  saveClients(next);
  appendActivity({
    id: `act_${Date.now()}`,
    clientId: q.clientId,
    at: new Date().toISOString(),
    type: 'payment_received',
    title: 'Payment recorded',
    detail: `Quote ${quoteId} marked paid.`,
  });
  appendNotification({
    id: `n_${Date.now()}`,
    type: 'payment',
    title: 'Payment received',
    message: `${q.clientSnapshot.name} — ${q.flight.airline} ${q.flight.flightNumber} ($${q.pricePerPerson})`,
    at: new Date().toISOString(),
    read: false,
  });
}

export function appendEmailLog(entry: EmailLogEntry) {
  const log = readJson<EmailLogEntry[]>(EMAIL_LOG_KEY, []);
  log.unshift(entry);
  writeJson(EMAIL_LOG_KEY, log.slice(0, 200));
}

export function loadEmailLog(): EmailLogEntry[] {
  return readJson<EmailLogEntry[]>(EMAIL_LOG_KEY, []);
}

const defaultCms: CmsDocument = {
  landingHeroTitle: 'Luxury flights, personal agents',
  landingHeroSubtitle: 'Tell us where you want to go — a Skylux agent builds the itinerary.',
  quoteEmailIntro: '',
  paymentReminderBody: 'Complete your secure payment to confirm your tickets.',
};

export function loadCms(): CmsDocument {
  return readJson<CmsDocument>(CMS_KEY, defaultCms);
}

export function saveCms(doc: CmsDocument) {
  writeJson(CMS_KEY, doc);
}

export function loadFlights(seed: Flight[] = []): Flight[] {
  const stored = readJson<Flight[] | null>(FLIGHTS_KEY, null);
  if (stored && Array.isArray(stored)) return stored;
  writeJson(FLIGHTS_KEY, seed);
  return seed;
}

export function saveFlights(flights: Flight[]) {
  writeJson(FLIGHTS_KEY, flights);
}

export function loadNotifications(): StoredNotification[] {
  return readJson<StoredNotification[]>(NOTIFICATIONS_KEY, []);
}

export function saveNotifications(rows: StoredNotification[]) {
  writeJson(NOTIFICATIONS_KEY, rows.slice(0, 100));
}

export function appendNotification(row: StoredNotification) {
  const rows = loadNotifications();
  rows.unshift(row);
  saveNotifications(rows);
}

export function markAllNotificationsRead() {
  const rows = loadNotifications().map((n) => ({ ...n, read: true }));
  saveNotifications(rows);
}

export function clearNotifications() {
  writeJson(NOTIFICATIONS_KEY, []);
}

export function loadActivities(): ClientActivity[] {
  return readJson<ClientActivity[]>(ACTIVITIES_KEY, []);
}

export function appendActivity(entry: ClientActivity) {
  const all = loadActivities();
  all.unshift(entry);
  writeJson(ACTIVITIES_KEY, all.slice(0, 300));
}

export function getActivitiesForClient(clientId: string): ClientActivity[] {
  return loadActivities().filter((a) => a.clientId === clientId);
}

export function logQuoteSent(opts: {
  agentId: string;
  clientId: string;
  clientName: string;
  flightLabel: string;
  pricePerPerson: number;
}) {
  appendActivity({
    id: `act_${Date.now()}`,
    clientId: opts.clientId,
    at: new Date().toISOString(),
    type: 'quote_sent',
    title: 'Quote sent',
    detail: `${opts.flightLabel} · $${opts.pricePerPerson} pp for ${opts.clientName}`,
  });
  appendNotification({
    id: `n_${Date.now()}`,
    type: 'quote_sent',
    title: 'Quote sent',
    message: `${opts.clientName} — ${opts.flightLabel} ($${opts.pricePerPerson})`,
    at: new Date().toISOString(),
    read: false,
  });
}

export function logClientCreated(clientId: string, name: string) {
  appendActivity({
    id: `act_${Date.now()}`,
    clientId,
    at: new Date().toISOString(),
    type: 'client_created',
    title: 'Client profile created',
    detail: name,
  });
}

export function logFlightInventoryChange(action: string, detail: string) {
  appendNotification({
    id: `n_${Date.now()}`,
    type: 'inventory',
    title: `Flights ${action}`,
    message: detail,
    at: new Date().toISOString(),
    read: false,
  });
}

export type AgentEmailBackupSmtpConfig = {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  pass: string;
  mailFrom: string;
  replyTo?: string;
};

export type AgentEmailBackupSettings = {
  enabled: boolean;
  smtp: AgentEmailBackupSmtpConfig;
};

type AgentEmailBackupMap = Record<string, AgentEmailBackupSettings>;

function loadAgentEmailBackupMap(): AgentEmailBackupMap {
  return readJson<AgentEmailBackupMap>(AGENT_EMAIL_BACKUP_KEY, {});
}

export function loadAgentEmailBackupSettings(agentId: string): AgentEmailBackupSettings {
  const map = loadAgentEmailBackupMap();
  const row = map[agentId];
  if (!row) {
    return {
      enabled: false,
      smtp: {
        host: '',
        port: 587,
        secure: false,
        user: '',
        pass: '',
        mailFrom: '',
        replyTo: '',
      },
    };
  }
  return {
    enabled: Boolean(row.enabled),
    smtp: {
      host: row.smtp?.host?.trim() || '',
      port: Number.isFinite(Number(row.smtp?.port)) ? Number(row.smtp?.port) : 587,
      secure: Boolean(row.smtp?.secure),
      user: row.smtp?.user?.trim() || '',
      pass: row.smtp?.pass || '',
      mailFrom: row.smtp?.mailFrom?.trim() || '',
      replyTo: row.smtp?.replyTo?.trim() || '',
    },
  };
}

export function saveAgentEmailBackupSettings(agentId: string, settings: AgentEmailBackupSettings) {
  const map = loadAgentEmailBackupMap();
  map[agentId] = {
    enabled: Boolean(settings.enabled),
    smtp: {
      host: settings.smtp.host.trim(),
      port: Number(settings.smtp.port) || 587,
      secure: Boolean(settings.smtp.secure),
      user: settings.smtp.user.trim(),
      pass: settings.smtp.pass,
      mailFrom: settings.smtp.mailFrom.trim(),
      replyTo: settings.smtp.replyTo?.trim() || '',
    },
  };
  writeJson(AGENT_EMAIL_BACKUP_KEY, map);
}

export function resolveBackupSmtpForCurrentRoute(): AgentEmailBackupSmtpConfig | undefined {
  if (typeof window === 'undefined') return undefined;
  const m = /^\/admin\/agent\/([^/]+)/.exec(window.location.pathname);
  if (!m?.[1]) return undefined;
  const row = loadAgentEmailBackupSettings(decodeURIComponent(m[1]));
  if (!row.enabled) return undefined;
  const cfg = row.smtp;
  if (!cfg.host || !cfg.user || !cfg.pass || !cfg.mailFrom) return undefined;
  return {
    host: cfg.host,
    port: Number(cfg.port) || 587,
    secure: Boolean(cfg.secure),
    user: cfg.user,
    pass: cfg.pass,
    mailFrom: cfg.mailFrom,
    replyTo: cfg.replyTo?.trim() || undefined,
  };
}
